import React from "react";
import { Document, documentTable } from "models/schemas";
import { datePopOver } from "utils/date-format";
import { Box, SpaceBetween } from "@amzn/awsui-components-react";

const isVersionedDocument = (doc: any): doc is VersionedDocument => {
  return (
    doc &&
    typeof doc === "object" &&
    "isLatest" in doc &&
    "versionNumber" in doc
  );
};

export const getVersionNumber = (
  doc: Document | VersionedDocument
): number | null => {
  if (isVersionedDocument(doc) && doc.totalVersions > 1) {
    return doc.versionNumber;
  }
  return null;
};

export interface VersionedDocument extends Document {
  readonly isLatest: boolean;
  readonly versionNumber: number;
  readonly totalVersions: number;
}

/**
 * Container for all versions of a single document
 */
export class DocumentVersions {
  constructor(private _latest: Document, private _older: Document[] = []) {}

  get latest(): VersionedDocument {
    return {
      ...this._latest,
      isLatest: true,
      versionNumber: this._older.length + 1,
      totalVersions: this._older.length + 1,
    };
  }

  get older(): VersionedDocument[] {
    return this._older.map((doc, index) => ({
      ...doc,
      isLatest: false,
      versionNumber: this._older.length - index,
      totalVersions: this._older.length + 1,
    }));
  }

  get allVersions(): VersionedDocument[] {
    return [this.latest, ...this.older];
  }

  get hasVersions(): boolean {
    return this._older.length > 0;
  }

  get versionCount(): number {
    return this._older.length + 1;
  }

  updateLatest(newDoc: Document): DocumentVersions {
    return new DocumentVersions(newDoc, this._older);
  }

  addOlderVersion(doc: Document): DocumentVersions {
    return new DocumentVersions(this._latest, [...this._older, doc]);
  }
}

/**
 * Collection managing all versioned documents
 */
export class DocumentCollection {
  constructor(private versionGroups: Map<string, DocumentVersions>) {}

  get versionGroupsMap(): Map<string, DocumentVersions> {
    return this.versionGroups;
  }

  get displayDocuments(): VersionedDocument[] {
    return Array.from(this.versionGroups.values()).map(
      (group: DocumentVersions) => group.latest
    );
  }

  get hasAnyVersions(): boolean {
    return Array.from(this.versionGroups.values()).some(
      (group: DocumentVersions) => group.hasVersions
    );
  }

  findDocumentById(id: string): VersionedDocument | null {
    const groups = Array.from(this.versionGroups.values());
    for (const group of groups) {
      const found = group.allVersions.find(
        (doc: VersionedDocument) => doc.documentId === id
      );
      if (found) return found;
    }
    return null;
  }

  getVersionsForDocument(doc: Document): VersionedDocument[] {
    const key = getVersionKey(doc);
    const group = this.versionGroups.get(key);
    return group ? group.allVersions : [];
  }

  updateDocument(
    docId: string,
    updates: Partial<Document>
  ): DocumentCollection {
    const newGroups = new Map(this.versionGroups);

    const entries = Array.from(newGroups.entries());
    for (const [key, group] of entries) {
      const updated = group.allVersions.map((doc: VersionedDocument) =>
        doc.documentId === docId ? { ...doc, ...updates } : doc
      );

      if (updated.some((doc: Document) => doc.documentId === docId)) {
        const [latest, ...older] = updated.sort(
          (a: Document, b: Document) =>
            Number(b.createdAt || 0) - Number(a.createdAt || 0)
        );
        newGroups.set(key, new DocumentVersions(latest, older));
        break;
      }
    }

    return new DocumentCollection(newGroups);
  }

  addDocument(doc: Document): DocumentCollection {
    const newGroups = new Map(this.versionGroups);
    const key = getVersionKey(doc);

    const existingGroup = newGroups.get(key);
    if (existingGroup) {
      const allDocs = [doc, ...existingGroup.allVersions];
      const sorted = allDocs.sort(
        (a, b) => Number(b.createdAt || 0) - Number(a.createdAt || 0)
      );
      const [latest, ...older] = sorted;
      newGroups.set(key, new DocumentVersions(latest, older));
    } else {
      newGroups.set(key, new DocumentVersions(doc));
    }

    return new DocumentCollection(newGroups);
  }

  removeDocument(docId: string): DocumentCollection {
    const newGroups = new Map<string, DocumentVersions>();

    const entries = Array.from(this.versionGroups.entries());
    for (const [key, group] of entries) {
      const remaining = group.allVersions.filter(
        (doc: VersionedDocument) => doc.documentId !== docId
      );

      if (remaining.length > 0) {
        const [latest, ...older] = remaining;
        newGroups.set(key, new DocumentVersions(latest, older));
      }
    }

    return new DocumentCollection(newGroups);
  }
}

/**
 * Creates DocumentCollection from array of documents
 */
export const createDocumentCollection = (
  documents: Document[]
): DocumentCollection => {
  const versionGroups = new Map<string, DocumentVersions>();

  const groupsByKey = new Map<string, Document[]>();
  documents.forEach((doc) => {
    const key = getVersionKey(doc);
    const group = groupsByKey.get(key) || [];
    group.push(doc);
    groupsByKey.set(key, group);
  });

  groupsByKey.forEach((docs, key) => {
    docs.sort((a, b) => Number(b.createdAt || 0) - Number(a.createdAt || 0));
    const latest = docs[0];
    const older = docs.length > 1 ? docs.slice(1) : [];
    versionGroups.set(key, new DocumentVersions(latest, older));
  });

  return new DocumentCollection(versionGroups);
};

/**
 * Generates a unique key for identifying document versions
 * @returns String key in format "filename.extension|documentType|parentId"
 */
export const getVersionKey = (document: Document): string => {
  return `${document.fileName}.${document.extentionType}|${document.documentType}|${document.parentId}`;
};

/**
 * Creates version labels for dropdown display
 * @returns Array of version labels sorted by version number descending
 */
export const createVersionLabels = (
  versions: VersionedDocument[]
): Array<{ label: string; value: string }> => {
  const sortedVersions = [...versions].sort(
    (a, b) => Number(b.createdAt || 0) - Number(a.createdAt || 0)
  );

  return sortedVersions.map((doc, index) => ({
    label: `Version ${sortedVersions.length - index} (${datePopOver(
      Number(doc.createdAt)
    )})${index === 0 ? " (CURRENT)" : ""}`,
    value: doc.documentId,
  }));
};

/**
 * Finds a document in either flat or grouped view mode
 */
export const findDocumentInAnyDisplayMode = (
  documentId: string,
  flatDocs: Document[],
  groupedDocs: documentTable[],
  documentCollection: DocumentCollection | null,
  displayMode: "flat" | "grouped",
  useAtlasEndpoints?: boolean
): Document | VersionedDocument | null => {
  if (useAtlasEndpoints) {
    // Atlas docs
    return flatDocs.find((doc) => doc.documentId === documentId) || null;
  }

  if (!documentCollection) return null;

  if (displayMode === "flat") {
    return documentCollection.findDocumentById(documentId);
  }

  // Grouped mode
  const found = groupedDocs
    .flatMap((group) => group.children || [])
    .find((doc) => doc.documentId === documentId);

  return found || documentCollection.findDocumentById(documentId);
};

export interface VersionCollisionInfo {
  hasCollision: boolean;
  fileName: string;
  wouldBecomeVersion: number;
  totalVersionsAfter: number;
  existingDoc?: VersionedDocument;
  sourceVersionCount?: number;
}

/**
 * Creates a "no collision" response for checkVersionCollision
 */
const createNoCollisionResult = (fileName: string): VersionCollisionInfo => ({
  hasCollision: false,
  fileName,
  wouldBecomeVersion: 1,
  totalVersionsAfter: 1,
});

/**
 * Checks if updating a document or uploading a new document would create a version collision
 * @param document The document being checked
 * @param newProperties The properties being changed (for updates). Pass {} for new documents
 * @param documentCollection The current document collection
 * @returns Collision information
 */
export const checkVersionCollision = (
  document: Document,
  newProperties: Partial<Document>,
  documentCollection: DocumentCollection | null
): VersionCollisionInfo => {
  if (!documentCollection) {
    return createNoCollisionResult(document.fileName);
  }
  const isNewDocument =
    !newProperties || Object.keys(newProperties).length === 0;

  const updatedDoc = { ...document, ...newProperties };
  const newVersionKey = getVersionKey(updatedDoc);

  if (!isNewDocument) {
    const currentVersionKey = getVersionKey(document);

    // No collision if the version key hasn't changed
    if (newVersionKey === currentVersionKey) {
      return createNoCollisionResult(document.fileName);
    }
  }

  // Check if the new version key matches an existing document group
  const versionGroups = documentCollection.versionGroupsMap;
  const existingVersionGroup = versionGroups.get(newVersionKey);

  if (existingVersionGroup) {
    const existingVersions = existingVersionGroup.allVersions;

    // get doc's existing versions
    let sourceVersionCount = 1;
    let sourceVersions: Document[] = [updatedDoc];

    if (!isNewDocument) {
      const currentVersionKey = getVersionKey(document);
      const sourceVersionGroup = versionGroups.get(currentVersionKey);
      if (sourceVersionGroup) {
        sourceVersionCount = sourceVersionGroup.versionCount;
        sourceVersions = sourceVersionGroup.allVersions.map((v) => ({
          ...v,
          ...newProperties,
        }));
      }
    }
    const allVersionsWithUpdate = [...existingVersions, ...sourceVersions];
    const sortedVersions = allVersionsWithUpdate.sort(
      (a, b) => Number(b.createdAt || 0) - Number(a.createdAt || 0)
    );
    const position = sortedVersions.findIndex(
      (doc) => doc.documentId === document.documentId
    );

    const wouldBecomeVersion = sortedVersions.length - position;
    const totalVersionsAfter = sortedVersions.length;

    return {
      hasCollision: true,
      fileName: document.fileName, // return original fileName in case of rename
      wouldBecomeVersion,
      totalVersionsAfter,
      existingDoc: existingVersionGroup.latest,
      sourceVersionCount,
    };
  }

  return createNoCollisionResult(document.fileName);
};

/**
 * Checks multiple documents for version collisions
 * @param documents Array of documents to check
 * @param getNewProperties Function that returns new properties for each document
 * @param documentCollection The current document collection
 * @returns Array of collision information for documents with collisions
 */
export const checkBatchVersionCollisions = (
  documents: Document[],
  getNewProperties: (doc: Document) => Partial<Document>,
  documentCollection: DocumentCollection | null
): VersionCollisionInfo[] => {
  const collisions: VersionCollisionInfo[] = [];

  documents.forEach((doc) => {
    const newProps = getNewProperties(doc);
    const collision = checkVersionCollision(doc, newProps, documentCollection);

    if (collision.hasCollision) {
      collisions.push(collision);
    }
  });

  return collisions;
};

/**
 * Renders a React element displaying version collision warning message
 * @param collisions Array of collision information
 * @returns React element with formatted message
 */
export const renderVersionCollisionMessage = (
  collisions: VersionCollisionInfo[]
): React.ReactElement => {
  if (collisions.length === 1) {
    const collision = collisions[0];
    const hasMultipleVersions = (collision.sourceVersionCount || 1) > 1;
    const olderVersionCount = (collision.sourceVersionCount || 1) - 1;

    return (
      <Box>
        {`Proceeding with this action will make this file${
          hasMultipleVersions
            ? ` and its ${olderVersionCount} existing version${
                olderVersionCount > 1 ? "s" : ""
              }`
            : ""
        } a version of an existing file.${
          hasMultipleVersions ? " The current version of" : ""
        } `}
        <b>{collision.fileName}</b>
        {` will become version `}
        <b>{collision.wouldBecomeVersion}</b>
        {` of `}
        <b>{collision.totalVersionsAfter}</b>
        {` total versions.`}
      </Box>
    );
  }

  const hasAnyMultipleVersions = collisions.some(
    (c) => (c.sourceVersionCount || 1) > 1
  );

  return (
    <SpaceBetween direction="vertical" size="s">
      <Box>
        {`Proceeding with this action will make these files${
          hasAnyMultipleVersions ? " and their existing versions" : ""
        } a version of existing files:`}
      </Box>
      <ul style={{ marginLeft: "20px" }}>
        {collisions.map((collision, index) => {
          const hasMultipleVersions = (collision.sourceVersionCount || 1) > 1;
          const olderVersionCount = (collision.sourceVersionCount || 1) - 1;

          return (
            <li key={index}>
              {hasMultipleVersions && <>{`The current version of `}</>}
              <b>{collision.fileName}</b>
              {hasMultipleVersions && (
                <>
                  {` (with ${olderVersionCount} existing version${
                    olderVersionCount > 1 ? "s" : ""
                  })`}
                </>
              )}
              {` will become version `}
              <b>{collision.wouldBecomeVersion}</b>
              {` of `}
              <b>{collision.totalVersionsAfter}</b>
              {` total versions.`}
            </li>
          );
        })}
      </ul>
    </SpaceBetween>
  );
};
